import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { RotateCcw, Search, ArrowLeft } from 'lucide-react';

const REASONS = ['Defective', 'Wrong item', 'Over supply', 'Other'];

export default function DebitNoteNew() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { formatMoney, taxLabel, currencySymbol } = useTenantFormat();
  const [searchParams] = useSearchParams();
  const presetPoId = searchParams.get('po_id') || '';

  const [poId, setPoId] = useState(presetPoId);
  const [po, setPo] = useState(null);
  const [poLoading, setPoLoading] = useState(false);

  const [poQuery, setPoQuery] = useState('');
  const [poResults, setPoResults] = useState([]);
  const [showPoDrop, setShowPoDrop] = useState(false);

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('Defective');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');
  const [returnQtys, setReturnQtys] = useState({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token || !poId) { setPo(null); return; }
    setPoLoading(true);
    api.purchaseOrderGet(token, poId)
      .then(r => { setPo(r); setReturnQtys({}); })
      .catch(e => { toast.show(e.message || 'PO not found', 'error'); setPo(null); })
      .finally(() => setPoLoading(false));
  }, [token, poId, toast]);

  useEffect(() => {
    if (!token || !poQuery.trim() || presetPoId) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.purchaseOrdersList(token, { page_size: 10 });
        const items = (r.items || []).filter(p =>
          `${p.po_number||''} ${p.supplier_name||''}`.toLowerCase().includes(poQuery.toLowerCase())
        );
        setPoResults(items.slice(0, 10));
      } catch { setPoResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [token, poQuery, presetPoId]);

  const lines = po?.items || [];

  const setQty = (productId, val) => {
    setReturnQtys(prev => ({ ...prev, [productId]: val }));
  };

  const refundTotal = useMemo(() => {
    return lines.reduce((sum, l) => {
      const qty = parseFloat(returnQtys[l.product_id] || 0);
      if (qty <= 0) return sum;
      const lineRefund = qty * l.unit_price * (1 + (l.gst_rate || 0) / 100);
      return sum + lineRefund;
    }, 0);
  }, [lines, returnQtys]);

  const returnAll = () => {
    const q = {};
    lines.forEach(l => { q[l.product_id] = l.qty_remaining ?? l.qty; });
    setReturnQtys(q);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const lineItems = lines
      .filter(l => parseFloat(returnQtys[l.product_id] || 0) > 0)
      .map(l => ({ product_id: l.product_id, qty_returned: parseFloat(returnQtys[l.product_id]) }));
    if (lineItems.length === 0) { setError('Select at least one item to return'); return; }
    const finalReason = reason === 'Other' ? customReason.trim() : reason;
    if (!finalReason) { setError('Reason is required'); return; }
    setSubmitting(true);
    try {
      const r = await api.debitNoteCreate(token, {
        po_id: poId,
        date,
        line_items: lineItems,
        reason: finalReason,
        notes: notes.trim() || null,
      });
      toast.show('Debit note created', 'success');
      navigate(`/notes/debit/${r.id || r.debit_note?.id || ''}`);
    } catch (err) {
      const msg = err?.message || 'Failed to create debit note';
      setError(msg);
      toast.show(msg, 'error');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="debit-note-new-page">
      <Link to="/notes/debit" className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-semibold hover:text-gray-700 mb-4">
        <ArrowLeft size={14} /> Back to Debit Notes
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center">
          <RotateCcw size={20} className="text-violet-600" style={{ transform: 'scaleX(-1)' }} />
        </div>
        <div>
          <h1 className="text-gray-900 font-extrabold text-xl">New Debit Note</h1>
          <p className="text-gray-500 text-xs">Return items to supplier</p>
        </div>
      </div>

      <GlassCard className="p-6">
        <form onSubmit={submit} className="space-y-5">
          {error && <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm" data-testid="dn-error">{error}</div>}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="dn-date"
                className="w-full h-11 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
            </div>
            <div>
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Reason</label>
              <div className="flex flex-wrap gap-1.5" data-testid="dn-reason-chips">
                {REASONS.map(r => (
                  <button key={r} type="button" onClick={() => setReason(r)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${reason === r ? 'bg-violet-50 border-violet-400 text-violet-700' : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'}`}>
                    {r}
                  </button>
                ))}
              </div>
              {reason === 'Other' && (
                <input type="text" value={customReason} onChange={e => setCustomReason(e.target.value)} placeholder="Specify reason..."
                  data-testid="dn-custom-reason" className="mt-2 w-full h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
              )}
            </div>
          </div>

          {!presetPoId && (
            <div className="relative">
              <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Purchase Order</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search by PO# or supplier..." value={poQuery}
                  onChange={e => { setPoQuery(e.target.value); setShowPoDrop(true); }}
                  onFocus={() => setShowPoDrop(true)}
                  data-testid="dn-po-search"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none" />
              </div>
              {showPoDrop && poResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto rounded-xl bg-white border border-gray-200 shadow-lg">
                  {poResults.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => { setPoId(p.id); setPoQuery(p.po_number || ''); setShowPoDrop(false); }}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-900 hover:bg-violet-50 border-b border-gray-100 last:border-0">
                      <span className="font-bold">{p.po_number}</span>
                      <span className="text-gray-400 ml-2">{p.supplier_name}</span>
                      <span className="text-gray-400 ml-2">{formatMoney(p.total)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {presetPoId && po && (
            <div className="px-4 py-2.5 rounded-xl bg-violet-50 border border-violet-200 text-violet-700 text-sm font-semibold">
              PO: {po.po_number} · {po.supplier_name} · {formatMoney(po.total)}
            </div>
          )}

          {poLoading ? (
            <div className="py-8 flex justify-center"><div className="w-5 h-5 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" /></div>
          ) : po && lines.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-gray-700 text-xs font-bold uppercase">Return Items</label>
                <button type="button" onClick={returnAll} data-testid="dn-return-all"
                  className="text-xs font-bold text-violet-600 hover:text-violet-800 transition-colors">
                  Return Entire PO
                </button>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm" data-testid="dn-lines-table">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-2.5 text-gray-500 text-xs font-bold uppercase">Product</th>
                      <th className="text-right px-4 py-2.5 text-gray-500 text-xs font-bold uppercase">Ordered</th>
                      <th className="text-right px-4 py-2.5 text-gray-500 text-xs font-bold uppercase">Returned</th>
                      <th className="text-right px-4 py-2.5 text-gray-500 text-xs font-bold uppercase">Remaining</th>
                      <th className="text-center px-4 py-2.5 text-gray-500 text-xs font-bold uppercase w-28">Return Qty</th>
                      <th className="text-right px-4 py-2.5 text-gray-500 text-xs font-bold uppercase">Price</th>
                      <th className="text-right px-4 py-2.5 text-gray-500 text-xs font-bold uppercase">{taxLabel || 'Tax'}</th>
                      <th className="text-right px-4 py-2.5 text-gray-500 text-xs font-bold uppercase">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map(l => {
                      const qtyRet = parseFloat(returnQtys[l.product_id] || 0);
                      const remaining = l.qty_remaining ?? (l.qty - (l.qty_returned || 0));
                      const lineRefund = qtyRet > 0 ? qtyRet * l.unit_price * (1 + (l.gst_rate || 0) / 100) : 0;
                      return (
                        <tr key={l.product_id} className="border-b border-gray-100">
                          <td className="px-4 py-2.5 text-gray-900 font-semibold">{l.product_name}</td>
                          <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">{l.qty}</td>
                          <td className="px-4 py-2.5 text-right text-red-500 tabular-nums font-semibold">{l.qty_returned || 0}</td>
                          <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">{remaining}</td>
                          <td className="px-4 py-2.5 text-center">
                            <input type="number" min={0} max={remaining} step="0.01"
                              value={returnQtys[l.product_id] ?? ''}
                              onChange={e => {
                                let v = parseFloat(e.target.value);
                                if (isNaN(v) || v < 0) v = '';
                                else if (v > remaining) v = remaining;
                                setQty(l.product_id, v === '' ? '' : v);
                              }}
                              disabled={remaining <= 0}
                              data-testid={`dn-qty-${l.product_id}`}
                              className="w-20 h-8 px-2 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm text-center tabular-nums focus:border-violet-400 outline-none disabled:opacity-40" />
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-700 tabular-nums">{formatMoney(l.unit_price)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-500 text-xs">{l.gst_rate}%</td>
                          <td className="px-4 py-2.5 text-right text-gray-900 font-semibold tabular-nums">{qtyRet > 0 ? formatMoney(lineRefund) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-3">
                <div className="px-5 py-3 rounded-xl bg-violet-50 border border-violet-200">
                  <p className="text-xs text-violet-400 font-bold uppercase">Refund Total</p>
                  <p className="text-violet-700 font-extrabold text-xl tabular-nums mt-0.5" data-testid="dn-refund-total">
                    {currencySymbol}{refundTotal.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          ) : po && lines.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-4">No line items on this PO</p>
          ) : null}

          <div>
            <label className="block text-gray-700 text-xs font-bold uppercase mb-1.5">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value.slice(0, 500))} maxLength={500} rows={2} placeholder="Additional notes..."
              data-testid="dn-notes"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm focus:border-brand-purple outline-none resize-none" />
          </div>

          <button type="submit" disabled={submitting || !po} data-testid="dn-submit"
            className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
            {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" /> : 'Create Debit Note'}
          </button>
        </form>
      </GlassCard>
    </div>
  );
}
